import type { Wall, WallWithTempId, Point, FloorPlanElement, Room, 
  WindowElement, DoorElement, RadiatorElement, BoilerElement, LightElement, 
  SmokeAlarmElement, UtilityElement, LoftHatchElement, TextElement, BayWindow, Compass } from '../types';
import { pointsAreEqual, isNearPoint, calculateRooms, getPersistentRoomId } from './floorPlanUtils';

const POINT_MATCH_TOLERANCE = 0.1;

export interface FileRepairResult {
  wallsRepaired: boolean;
  elementsRepaired: boolean;
  roomsRepaired: boolean;
  issuesFound: string[];
  issuesFixed: string[];
  repairedWalls: WallWithTempId[];
  repairedElements: FloorPlanElement[];
  repairedRoomLabels: Record<string, string>;
  reportSummary: {
    totalWalls: number;
    totalElements: number;
    wallsWithIssues: number;
    elementsWithIssues: number;
    wallsFixed: number;
    elementsFixed: number;
    extrusionsFixed: number;
    legacyParentWallIdsFixed: number;
    orphanedWallsRemoved: number;
    duplicateWallsRemoved: number;
    tempIdsGenerated: number;
    elementIdsGenerated: number;
    brokenElementReferencesFixed: number;
    elementPropertiesFixed: number;
    unnamedRoomsLabeled: number;
    incompleteRoomsCompleted: number;
    missingWallsAdded: number;
    elementsRelocatedToValidFloors: number;
  };
}

export interface RepairOptions {
  fixExtrusionIssues: boolean;
  fixLegacyParentWallIds: boolean;
  removeOrphanedWalls: boolean;
  removeDuplicateWalls: boolean;
  generateMissingTempIds: boolean;
  generateMissingElementIds: boolean;
  fixBrokenElementReferences: boolean;
  fixElementProperties: boolean;
  validateGeometry: boolean;
  fixZeroLengthWalls: boolean;
  labelUnnamedRooms: boolean;
  completeIncompleteRooms: boolean;
  relocateElementsToValidFloors: boolean;
  preserveAllElementTypes: boolean;
}

export const DEFAULT_REPAIR_OPTIONS: RepairOptions = {
  fixExtrusionIssues: true,
  fixLegacyParentWallIds: true,
  removeOrphanedWalls: true,
  removeDuplicateWalls: true,
  generateMissingTempIds: true,
  generateMissingElementIds: true,
  fixBrokenElementReferences: true,
  fixElementProperties: true,
  validateGeometry: true,
  fixZeroLengthWalls: true,
  labelUnnamedRooms: true,
  completeIncompleteRooms: true,
  relocateElementsToValidFloors: true,
  preserveAllElementTypes: true,
};

/**
 * Main file repair function that fixes all common issues with old floor plan files
 */
export const repairFloorPlanFile = (
  walls: Wall[],
  elements: FloorPlanElement[],
  options: RepairOptions = DEFAULT_REPAIR_OPTIONS,
  existingRoomLabels: Record<string, string> = {}
): FileRepairResult => {
  const result: FileRepairResult = {
    wallsRepaired: false,
    elementsRepaired: false,
    roomsRepaired: false,
    issuesFound: [],
    issuesFixed: [],
    repairedWalls: [],
    repairedElements: [],
    repairedRoomLabels: { ...existingRoomLabels },
    reportSummary: {
      totalWalls: walls.length,
      totalElements: elements.length,
      wallsWithIssues: 0,
      elementsWithIssues: 0,
      wallsFixed: 0,
      elementsFixed: 0,
      extrusionsFixed: 0,
      legacyParentWallIdsFixed: 0,
      orphanedWallsRemoved: 0,
      duplicateWallsRemoved: 0,
      tempIdsGenerated: 0,
      elementIdsGenerated: 0,
      brokenElementReferencesFixed: 0,
      elementPropertiesFixed: 0,
      unnamedRoomsLabeled: 0,
      incompleteRoomsCompleted: 0,
      missingWallsAdded: 0,
      elementsRelocatedToValidFloors: 0,
    }
  };

  let workingWalls = [...walls] as WallWithTempId[];
  let workingElements = [...elements];

  console.log('[FileRepair] Starting comprehensive repair process...', {
    totalWalls: workingWalls.length,
    totalElements: workingElements.length,
    elementTypes: getElementTypeBreakdown(workingElements),
    options
  });

  // Step 1: Generate missing tempIds for walls
  if (options.generateMissingTempIds) {
    const { walls: wallsWithTempIds, issuesFound, issuesFixed, idsGenerated } = generateMissingTempIds(workingWalls);
    workingWalls = wallsWithTempIds;
    result.issuesFound.push(...issuesFound);
    result.issuesFixed.push(...issuesFixed);
    result.reportSummary.tempIdsGenerated = idsGenerated;
    if (idsGenerated > 0) result.wallsRepaired = true;
  }

  // Step 2: Generate missing IDs for elements
  if (options.generateMissingElementIds) {
    const { elements: elementsWithIds, issuesFound, issuesFixed, idsGenerated } = generateMissingElementIds(workingElements);
    workingElements = elementsWithIds;
    result.issuesFound.push(...issuesFound);
    result.issuesFixed.push(...issuesFixed);
    result.reportSummary.elementIdsGenerated = idsGenerated;
    if (idsGenerated > 0) result.elementsRepaired = true;
  }

  // Step 3: Fix element properties (positions, floors, etc.)
  if (options.fixElementProperties) {
    const { elements: fixedElements, issuesFound, issuesFixed, propertiesFixed } = fixElementProperties(workingElements, workingWalls);
    workingElements = fixedElements;
    result.issuesFound.push(...issuesFound);
    result.issuesFixed.push(...issuesFixed);
    result.reportSummary.elementPropertiesFixed = propertiesFixed;
    if (propertiesFixed > 0) result.elementsRepaired = true;
  }

  // Step 4: Fix zero-length walls
  if (options.fixZeroLengthWalls) {
    const { walls: fixedWalls, issuesFound, issuesFixed, wallsRemoved } = fixZeroLengthWalls(workingWalls);
    workingWalls = fixedWalls;
    result.issuesFound.push(...issuesFound);
    result.issuesFixed.push(...issuesFixed);
    result.reportSummary.wallsFixed += wallsRemoved;
    if (wallsRemoved > 0) result.wallsRepaired = true;
  }

  // Step 5: Remove duplicate walls
  if (options.removeDuplicateWalls) {
    const { walls: deduplicatedWalls, issuesFound, issuesFixed, duplicatesRemoved } = removeDuplicateWalls(workingWalls);
    workingWalls = deduplicatedWalls;
    result.issuesFound.push(...issuesFound);
    result.issuesFixed.push(...issuesFixed);
    result.reportSummary.duplicateWallsRemoved = duplicatesRemoved;
    if (duplicatesRemoved > 0) result.wallsRepaired = true;
  }

  // Step 6: Fix extrusion issues (includes legacy parent wall ID fix)
  if (options.fixExtrusionIssues) {
    const { walls: fixedExtrusionWalls, issuesFound, issuesFixed, extrusionsFixed } = fixExtrusionIssues(workingWalls);
    workingWalls = fixedExtrusionWalls;
    result.issuesFound.push(...issuesFound);
    result.issuesFixed.push(...issuesFixed);
    result.reportSummary.extrusionsFixed = extrusionsFixed;
    result.reportSummary.legacyParentWallIdsFixed = extrusionsFixed; // Legacy fixes are counted as extrusion fixes
    if (extrusionsFixed > 0) result.wallsRepaired = true;
  }

  // Step 7: Remove orphaned walls
  if (options.removeOrphanedWalls) {
    const { walls: cleanedWalls, issuesFound, issuesFixed, orphansRemoved } = removeOrphanedWalls(workingWalls);
    workingWalls = cleanedWalls;
    result.issuesFound.push(...issuesFound);
    result.issuesFixed.push(...issuesFixed);
    result.reportSummary.orphanedWallsRemoved = orphansRemoved;
    if (orphansRemoved > 0) result.wallsRepaired = true;
  }

  // Step 8: Fix broken element references (after wall changes)
  if (options.fixBrokenElementReferences) {
    const { elements: fixedElements, issuesFound, issuesFixed, referencesFixed } = fixBrokenElementReferences(workingElements, workingWalls);
    workingElements = fixedElements;
    result.issuesFound.push(...issuesFound);
    result.issuesFixed.push(...issuesFixed);
    result.reportSummary.brokenElementReferencesFixed = referencesFixed;
    if (referencesFixed > 0) result.elementsRepaired = true;
  }

  // Step 9: Relocate elements to valid floors
  if (options.relocateElementsToValidFloors) {
    const { elements: relocatedElements, issuesFound, issuesFixed, elementsRelocated } = relocateElementsToValidFloors(workingElements, workingWalls);
    workingElements = relocatedElements;
    result.issuesFound.push(...issuesFound);
    result.issuesFixed.push(...issuesFixed);
    result.reportSummary.elementsRelocatedToValidFloors = elementsRelocated;
    if (elementsRelocated > 0) result.elementsRepaired = true;
  }

  // Step 10: Validate geometry
  if (options.validateGeometry) {
    const { walls: validatedWalls, issuesFound, issuesFixed } = validateWallGeometry(workingWalls);
    workingWalls = validatedWalls;
    result.issuesFound.push(...issuesFound);
    result.issuesFixed.push(...issuesFixed);
  }

  // Step 11: Complete incomplete rooms
  if (options.completeIncompleteRooms) {
    const { walls: completedWalls, issuesFound, issuesFixed, roomsCompleted, wallsAdded } = completeIncompleteRooms(workingWalls);
    workingWalls = completedWalls;
    result.issuesFound.push(...issuesFound);
    result.issuesFixed.push(...issuesFixed);
    result.reportSummary.incompleteRoomsCompleted = roomsCompleted;
    result.reportSummary.missingWallsAdded = wallsAdded;
    if (roomsCompleted > 0) result.wallsRepaired = true;
  }

  // Step 12: Label unnamed rooms
  if (options.labelUnnamedRooms) {
    const { roomLabels, issuesFound, issuesFixed, roomsLabeled } = labelUnnamedRooms(workingWalls, result.repairedRoomLabels);
    result.repairedRoomLabels = roomLabels;
    result.issuesFound.push(...issuesFound);
    result.issuesFixed.push(...issuesFixed);
    result.reportSummary.unnamedRoomsLabeled = roomsLabeled;
    if (roomsLabeled > 0) result.roomsRepaired = true;
  }

  result.repairedWalls = workingWalls;
  result.repairedElements = workingElements;
  result.reportSummary.wallsWithIssues = result.issuesFound.filter(issue => issue.includes('Wall')).length;
  result.reportSummary.elementsWithIssues = result.issuesFound.filter(issue => issue.includes('Element')).length;

  console.log('[FileRepair] Comprehensive repair process completed', {
    ...result.reportSummary,
    finalElementTypes: getElementTypeBreakdown(workingElements)
  });

  return result;
};

/**
 * Get a breakdown of element types for debugging
 */
const getElementTypeBreakdown = (elements: FloorPlanElement[]): Record<string, number> => {
  const breakdown: Record<string, number> = {};
  elements.forEach(element => {
    breakdown[element.type] = (breakdown[element.type] || 0) + 1;
  });
  return breakdown;
};

/**
 * Generate missing tempIds for walls
 */
const generateMissingTempIds = (walls: WallWithTempId[]): {
  walls: WallWithTempId[];
  issuesFound: string[];
  issuesFixed: string[];
  idsGenerated: number;
} => {
  const issuesFound: string[] = [];
  const issuesFixed: string[] = [];
  let idsGenerated = 0;

  const fixedWalls = walls.map((wall, index) => {
    if (!wall.tempId || wall.tempId === '') {
      const newTempId = `wall_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      issuesFound.push(`Wall at index ${index} missing tempId`);
      issuesFixed.push(`Generated tempId for wall: ${newTempId}`);
      idsGenerated++;
      return { ...wall, tempId: newTempId };
    }
    return wall;
  });

  return { walls: fixedWalls, issuesFound, issuesFixed, idsGenerated };
};

/**
 * Generate missing element IDs for all element types
 */
const generateMissingElementIds = (elements: FloorPlanElement[]): {
  elements: FloorPlanElement[];
  issuesFound: string[];
  issuesFixed: string[];
  idsGenerated: number;
} => {
  const issuesFound: string[] = [];
  const issuesFixed: string[] = [];
  let idsGenerated = 0;

  const fixedElements = elements.map((element, index) => {
    // Check if element has an ID (some element types require it)
    const needsId = 'id' in element && element.id;
    const hasValidId = needsId && typeof element.id === 'string' && element.id.length > 0;

    if ('id' in element && !hasValidId) {
      const elementType = element.type;
      const newId = `${elementType}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      issuesFound.push(`Element ${elementType} at index ${index} missing valid ID`);
      issuesFixed.push(`Generated ID for ${elementType}: ${newId}`);
      idsGenerated++;

      return { ...element, id: newId };
    }

    return element;
  });

  return { elements: fixedElements, issuesFound, issuesFixed, idsGenerated };
};

/**
 * Fix element properties for all elements, ensuring they have valid positions, floors, and dimensions
 */
const fixElementProperties = (elements: FloorPlanElement[], walls: WallWithTempId[]): {
  elements: FloorPlanElement[];
  issuesFound: string[];
  issuesFixed: string[];
  propertiesFixed: number;
} => {
  const issuesFound: string[] = [];
  const issuesFixed: string[] = [];
  let propertiesFixed = 0;

  // Get available floors from walls for better floor assignment
  const availableFloors = new Set<number>();
  walls.forEach(wall => {
    if (wall.floor && wall.floor >= 1) {
      availableFloors.add(wall.floor);
    }
  });
  const sortedFloors = Array.from(availableFloors).sort((a, b) => a - b);
  const defaultFloor = sortedFloors.length > 0 ? sortedFloors[0] : 1;

  console.log('[FileRepair] Element property fixing starting', {
    totalElements: elements.length,
    availableFloors: sortedFloors,
    defaultFloor
  });

  const fixedElements = elements.map((element, index) => {
    let fixedElement = { ...element };
    let elementFixed = false;

    // Generate missing ID if needed (only for elements that should have IDs)
    const elementTypesWithIds = ['window', 'door', 'radiator', 'boiler', 'light', 'smoke-alarm', 'utility', 'loft-hatch'];
    if (elementTypesWithIds.includes(element.type)) {
      const elementWithId = element as any;
      if (!elementWithId.id || typeof elementWithId.id !== 'string') {
        issuesFound.push(`Element ${element.type} at index ${index} missing id`);
        (fixedElement as any).id = `${element.type}-${Date.now()}-${index}`;
        issuesFixed.push(`Generated id for ${element.type}`);
        elementFixed = true;
      }
    }

    // Fix invalid positions for elements that have position property
    if ('position' in element) {
      if (!element.position || 
          typeof element.position.x !== 'number' || 
          typeof element.position.y !== 'number' ||
          !isFinite(element.position.x) || 
          !isFinite(element.position.y)) {
        issuesFound.push(`Element ${element.type} at index ${index} has invalid position`);
        (fixedElement as any).position = { x: 0, y: 0 };
        issuesFixed.push(`Reset position for ${element.type} to origin`);
        elementFixed = true;
      }
    }

    // Fix invalid floors for elements that have floor property
    if ('floor' in element) {
      const currentFloor = (element as any).floor;
      if (!currentFloor || currentFloor < 1 || !Number.isInteger(currentFloor) || !availableFloors.has(currentFloor)) {
        issuesFound.push(`Element ${element.type} at index ${index} has invalid floor: ${currentFloor}`);
        
        // Try to determine correct floor from associated wall if element has wallId
        let targetFloor = defaultFloor;
        if ('wallId' in element && (element as any).wallId) {
          const associatedWall = walls.find(w => w.tempId === (element as any).wallId || w.id === (element as any).wallId);
          if (associatedWall && associatedWall.floor && availableFloors.has(associatedWall.floor)) {
            targetFloor = associatedWall.floor;
            issuesFixed.push(`Assigned ${element.type} to floor ${targetFloor} based on associated wall`);
          }
        }
        
        (fixedElement as any).floor = targetFloor;
        issuesFixed.push(`Reset floor for ${element.type} to ${targetFloor}`);
        elementFixed = true;
      }
    }

    // Element-specific fixes
    switch (element.type) {
      case 'window':
        fixedElement = fixWindowElement(fixedElement as WindowElement, issuesFound, issuesFixed, index);
        break;
      case 'door':
        fixedElement = fixDoorElement(fixedElement as DoorElement, issuesFound, issuesFixed, index);
        break;
      case 'radiator':
        fixedElement = fixRadiatorElement(fixedElement as RadiatorElement, issuesFound, issuesFixed, index);
        break;
      case 'boiler':
        fixedElement = fixBoilerElement(fixedElement as BoilerElement, issuesFound, issuesFixed, index);
        break;
      case 'light':
        fixedElement = fixLightElement(fixedElement as LightElement, issuesFound, issuesFixed, index);
        break;
      case 'smoke-alarm':
        fixedElement = fixSmokeAlarmElement(fixedElement as SmokeAlarmElement, issuesFound, issuesFixed, index);
        break;
      case 'utility':
        fixedElement = fixUtilityElement(fixedElement as UtilityElement, issuesFound, issuesFixed, index);
        break;
      case 'loft-hatch':
        fixedElement = fixLoftHatchElement(fixedElement as LoftHatchElement, issuesFound, issuesFixed, index);
        break;
      case 'text':
        fixedElement = fixTextElement(fixedElement as TextElement, issuesFound, issuesFixed, index);
        break;
      case 'bayWindow':
        fixedElement = fixBayWindowElement(fixedElement as BayWindow, issuesFound, issuesFixed, index);
        break;
      case 'compass':
        fixedElement = fixCompassElement(fixedElement as Compass, issuesFound, issuesFixed, index);
        break;
    }

    if (elementFixed || fixedElement !== element) {
      propertiesFixed++;
    }

    return fixedElement;
  });

  console.log('[FileRepair] Element property fixing completed', {
    totalElements: elements.length,
    propertiesFixed,
    issuesFound: issuesFound.length,
    issuesFixed: issuesFixed.length
  });

  return { elements: fixedElements, issuesFound, issuesFixed, propertiesFixed };
};

/**
 * Element-specific repair functions
 */
const fixWindowElement = (element: WindowElement, issuesFound: string[], issuesFixed: string[], index: number): WindowElement => {
  let fixed = { ...element };

  if (!element.wallId || typeof element.wallId !== 'string') {
    issuesFound.push(`Window at index ${index} missing wallId`);
    fixed.wallId = 'wall_unknown';
    issuesFixed.push(`Set placeholder wallId for window`);
  }

  if (typeof element.positionOnWall !== 'number' || element.positionOnWall < 0 || element.positionOnWall > 1) {
    issuesFound.push(`Window at index ${index} has invalid positionOnWall: ${element.positionOnWall}`);
    fixed.positionOnWall = 0.5;
    issuesFixed.push(`Reset positionOnWall for window to 0.5`);
  }

  if (typeof element.width !== 'number' || element.width <= 0) {
    issuesFound.push(`Window at index ${index} has invalid width: ${element.width}`);
    fixed.width = 100;
    issuesFixed.push(`Reset width for window to 100`);
  }

  if (typeof element.height !== 'number' || element.height <= 0) {
    issuesFound.push(`Window at index ${index} has invalid height: ${element.height}`);
    fixed.height = 100;
    issuesFixed.push(`Reset height for window to 100`);
  }



  return fixed;
};

const fixDoorElement = (element: DoorElement, issuesFound: string[], issuesFixed: string[], index: number): DoorElement => {
  let fixed = { ...element };

  if (!element.wallId || typeof element.wallId !== 'string') {
    issuesFound.push(`Door at index ${index} missing wallId`);
    fixed.wallId = 'wall_unknown';
    issuesFixed.push(`Set placeholder wallId for door`);
  }

  if (typeof element.positionOnWall !== 'number' || element.positionOnWall < 0 || element.positionOnWall > 1) {
    issuesFound.push(`Door at index ${index} has invalid positionOnWall: ${element.positionOnWall}`);
    fixed.positionOnWall = 0.5;
    issuesFixed.push(`Reset positionOnWall for door to 0.5`);
  }

  if (typeof element.width !== 'number' || element.width <= 0) {
    issuesFound.push(`Door at index ${index} has invalid width: ${element.width}`);
    fixed.width = 80;
    issuesFixed.push(`Reset width for door to 80`);
  }

  if (typeof element.swingRight !== 'boolean') {
    issuesFound.push(`Door at index ${index} has invalid swingRight: ${element.swingRight}`);
    fixed.swingRight = true;
    issuesFixed.push(`Reset swingRight for door to true`);
  }

  if (typeof element.opensIn !== 'boolean') {
    issuesFound.push(`Door at index ${index} has invalid opensIn: ${element.opensIn}`);
    fixed.opensIn = true;
    issuesFixed.push(`Reset opensIn for door to true`);
  }

  return fixed;
};

const fixRadiatorElement = (element: RadiatorElement, issuesFound: string[], issuesFixed: string[], index: number): RadiatorElement => {
  let fixed = { ...element };

  if (!element.wallId || typeof element.wallId !== 'string') {
    issuesFound.push(`Radiator at index ${index} missing wallId`);
    fixed.wallId = 'wall_unknown';
    issuesFixed.push(`Set placeholder wallId for radiator`);
  }

  if (!element.radiatorType || !['with-trvs', 'without-trvs', 'panel-heater', 'fan-heater', 'electric-storage-heater', 'high-heat-retention'].includes(element.radiatorType)) {
    issuesFound.push(`Radiator at index ${index} has invalid radiatorType: ${element.radiatorType}`);
    fixed.radiatorType = 'with-trvs';
    issuesFixed.push(`Reset radiatorType for radiator to 'with-trvs'`);
  }

  return fixed;
};

const fixBoilerElement = (element: BoilerElement, issuesFound: string[], issuesFixed: string[], index: number): BoilerElement => {
  let fixed = { ...element };

  if (!element.wallId || typeof element.wallId !== 'string') {
    issuesFound.push(`Boiler at index ${index} missing wallId`);
    fixed.wallId = 'wall_unknown';
    issuesFixed.push(`Set placeholder wallId for boiler`);
  }

  if (!element.heatingType || !['combi', 'system', 'regular', 'other'].includes(element.heatingType)) {
    issuesFound.push(`Boiler at index ${index} has invalid heatingType: ${element.heatingType}`);
    fixed.heatingType = 'combi';
    issuesFixed.push(`Reset heatingType for boiler to 'combi'`);
  }

  if (!element.brand || typeof element.brand !== 'string') {
    fixed.brand = 'Unknown';
    issuesFixed.push(`Set default brand for boiler`);
  }

  if (!element.model || typeof element.model !== 'string') {
    fixed.model = 'Unknown';
    issuesFixed.push(`Set default model for boiler`);
  }

  if (!element.gcNumber || typeof element.gcNumber !== 'string') {
    fixed.gcNumber = 'Unknown';
    issuesFixed.push(`Set default gcNumber for boiler`);
  }

  return fixed;
};

const fixLightElement = (element: LightElement, issuesFound: string[], issuesFixed: string[], index: number): LightElement => {
  let fixed = { ...element };

  if (!element.lightType || !['low-energy', 'non-low-energy'].includes(element.lightType)) {
    issuesFound.push(`Light at index ${index} has invalid lightType: ${element.lightType}`);
    fixed.lightType = 'low-energy';
    issuesFixed.push(`Reset lightType for light to 'low-energy'`);
  }

  return fixed;
};

const fixSmokeAlarmElement = (element: SmokeAlarmElement, issuesFound: string[], issuesFixed: string[], index: number): SmokeAlarmElement => {
  // Smoke alarms have minimal properties, just validate the basics
  return element;
};

const fixUtilityElement = (element: UtilityElement, issuesFound: string[], issuesFixed: string[], index: number): UtilityElement => {
  let fixed = { ...element };

  const validUtilityTypes = ['gas-meter', 'electric-meter', 'stop-tap', 'tank-cylinder', 'consumer-unit', 'fan', 'background-vent', 'thermostat', 'air-source-hp'];
  if (!element.utilityType || !validUtilityTypes.includes(element.utilityType)) {
    issuesFound.push(`Utility at index ${index} has invalid utilityType: ${element.utilityType}`);
    fixed.utilityType = 'gas-meter';
    issuesFixed.push(`Reset utilityType for utility to 'gas-meter'`);
  }

  return fixed;
};

const fixLoftHatchElement = (element: LoftHatchElement, issuesFound: string[], issuesFixed: string[], index: number): LoftHatchElement => {
  let fixed = { ...element };

  if (typeof element.width !== 'number' || element.width <= 0) {
    issuesFound.push(`Loft hatch at index ${index} has invalid width: ${element.width}`);
    fixed.width = 60;
    issuesFixed.push(`Reset width for loft hatch to 60`);
  }

  if (typeof element.height !== 'number' || element.height <= 0) {
    issuesFound.push(`Loft hatch at index ${index} has invalid height: ${element.height}`);
    fixed.height = 60;
    issuesFixed.push(`Reset height for loft hatch to 60`);
  }

  return fixed;
};

const fixTextElement = (element: TextElement, issuesFound: string[], issuesFixed: string[], index: number): TextElement => {
  let fixed = { ...element };

  if (!element.content || typeof element.content !== 'string') {
    issuesFound.push(`Text element at index ${index} has invalid content`);
    fixed.content = 'Text';
    issuesFixed.push(`Reset content for text element to 'Text'`);
  }

  if (typeof element.fontSize !== 'number' || element.fontSize <= 0) {
    issuesFound.push(`Text element at index ${index} has invalid fontSize: ${element.fontSize}`);
    fixed.fontSize = 14;
    issuesFixed.push(`Reset fontSize for text element to 14`);
  }

  if (typeof element.rotation !== 'number') {
    issuesFound.push(`Text element at index ${index} has invalid rotation: ${element.rotation}`);
    fixed.rotation = 0;
    issuesFixed.push(`Reset rotation for text element to 0`);
  }

  return fixed;
};

const fixBayWindowElement = (element: BayWindow, issuesFound: string[], issuesFixed: string[], index: number): BayWindow => {
  let fixed = { ...element };

  if (!element.style || !['canted', 'square', 'shallow'].includes(element.style)) {
    issuesFound.push(`Bay window at index ${index} has invalid style: ${element.style}`);
    fixed.style = 'canted';
    issuesFixed.push(`Reset style for bay window to 'canted'`);
  }

  if (typeof element.width !== 'number' || element.width <= 0) {
    issuesFound.push(`Bay window at index ${index} has invalid width: ${element.width}`);
    fixed.width = 100;
    issuesFixed.push(`Reset width for bay window to 100`);
  }

  if (typeof element.depth !== 'number' || element.depth <= 0) {
    issuesFound.push(`Bay window at index ${index} has invalid depth: ${element.depth}`);
    fixed.depth = 50;
    issuesFixed.push(`Reset depth for bay window to 50`);
  }

  return fixed;
};

const fixCompassElement = (element: Compass, issuesFound: string[], issuesFixed: string[], index: number): Compass => {
  let fixed = { ...element };

  if (typeof element.rotation !== 'number') {
    issuesFound.push(`Compass at index ${index} has invalid rotation: ${element.rotation}`);
    fixed.rotation = 0;
    issuesFixed.push(`Reset rotation for compass to 0`);
  }

  if (typeof element.size !== 'number' || element.size <= 0) {
    issuesFound.push(`Compass at index ${index} has invalid size: ${element.size}`);
    fixed.size = 20;
    issuesFixed.push(`Reset size for compass to 20`);
  }

  return fixed;
};

/**
 * Relocate elements to valid floors based on their wall associations and room context
 */
const relocateElementsToValidFloors = (elements: FloorPlanElement[], walls: WallWithTempId[]): {
  elements: FloorPlanElement[];
  issuesFound: string[];
  issuesFixed: string[];
  elementsRelocated: number;
} => {
  const issuesFound: string[] = [];
  const issuesFixed: string[] = [];
  let elementsRelocated = 0;

  // Group walls by floor for floor-aware processing
  const wallsByFloor = new Map<number, WallWithTempId[]>();
  walls.forEach(wall => {
    if (wall.floor && wall.floor >= 1) {
      if (!wallsByFloor.has(wall.floor)) {
        wallsByFloor.set(wall.floor, []);
      }
      wallsByFloor.get(wall.floor)!.push(wall);
    }
  });

  console.log('[FileRepair] Floor-aware element relocation starting', {
    totalElements: elements.length,
    availableFloors: Array.from(wallsByFloor.keys()).sort((a, b) => a - b),
    floorWallCounts: Array.from(wallsByFloor.entries()).map(([floor, walls]) => ({ floor, wallCount: walls.length }))
  });

  const relocatedElements = elements.map((element, index) => {
    let fixedElement = { ...element };
    let wasRelocated = false;

    // Only process elements that have floor property
    if (!('floor' in element)) {
      return fixedElement;
    }

    const currentFloor = (element as any).floor;

    // Check if element is on a valid floor
    if (!wallsByFloor.has(currentFloor)) {
      issuesFound.push(`Element ${element.type} at index ${index} is on invalid floor ${currentFloor}`);
      
      // Try to find correct floor based on element associations
      let targetFloor: number | null = null;

      // For wall-attached elements, find the correct floor from associated wall
      if ('wallId' in element && (element as any).wallId) {
        const wallId = (element as any).wallId;
        const associatedWall = walls.find(w => w.tempId === wallId || w.id === wallId);
        
        if (associatedWall && associatedWall.floor && wallsByFloor.has(associatedWall.floor)) {
          targetFloor = associatedWall.floor;
          issuesFixed.push(`Relocated ${element.type} to floor ${targetFloor} based on associated wall ${wallId}`);
        }
      }

      // For positioned elements, find floor by checking which floor's rooms contain the element
      if (!targetFloor && 'position' in element && (element as any).position) {
        const elementPosition = (element as any).position as Point;
        
        // Check each floor to see if element position is within any room
        for (const [floor, floorWalls] of wallsByFloor.entries()) {
          try {
            const rooms = calculateRooms(floorWalls, {}, 'explicitRoomCreation', floor);
            const containingRoom = rooms.find(room => {
              // Import isPointInsideRoom from floorPlanUtils
              const { isPointInsideRoom } = require('./floorPlanUtils');
              return isPointInsideRoom(elementPosition, room.walls);
            });
            
            if (containingRoom) {
              targetFloor = floor;
              issuesFixed.push(`Relocated ${element.type} to floor ${targetFloor} based on position within room "${containingRoom.name}"`);
              break;
            }
          } catch (error) {
            // Skip room calculation errors for this floor
          }
        }
      }

      // Fallback to first available floor
      if (!targetFloor) {
        const firstFloor = Math.min(...wallsByFloor.keys());
        targetFloor = firstFloor;
        issuesFixed.push(`Relocated ${element.type} to floor ${targetFloor} (fallback to first available floor)`);
      }

      if (targetFloor && targetFloor !== currentFloor) {
        (fixedElement as any).floor = targetFloor;
        elementsRelocated++;
        wasRelocated = true;
      }
    }

    return fixedElement;
  });

  console.log('[FileRepair] Floor-aware element relocation completed', {
    elementsRelocated,
    issuesFound: issuesFound.length,
    issuesFixed: issuesFixed.length
  });

  return { elements: relocatedElements, issuesFound, issuesFixed, elementsRelocated };
};

/**
 * Fix zero-length walls that can cause rendering issues
 */
const fixZeroLengthWalls = (walls: WallWithTempId[]): {
  walls: WallWithTempId[];
  issuesFound: string[];
  issuesFixed: string[];
  wallsRemoved: number;
} => {
  const issuesFound: string[] = [];
  const issuesFixed: string[] = [];
  let wallsRemoved = 0;

  const validWalls = walls.filter(wall => {
    const length = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
    if (length < 0.1) { // Zero or near-zero length
      issuesFound.push(`Zero-length wall found: ${wall.tempId}`);
      issuesFixed.push(`Removed zero-length wall: ${wall.tempId}`);
      wallsRemoved++;
      return false;
    }
    return true;
  });

  return { walls: validWalls, issuesFound, issuesFixed, wallsRemoved };
};

/**
 * Remove duplicate walls that can cause performance issues
 */
const removeDuplicateWalls = (walls: WallWithTempId[]): {
  walls: WallWithTempId[];
  issuesFound: string[];
  issuesFixed: string[];
  duplicatesRemoved: number;
} => {
  const issuesFound: string[] = [];
  const issuesFixed: string[] = [];
  let duplicatesRemoved = 0;

  const uniqueWalls: WallWithTempId[] = [];
  const seenWalls = new Set<string>();

  walls.forEach(wall => {
    const wallKey = `${wall.start.x.toFixed(2)}_${wall.start.y.toFixed(2)}_${wall.end.x.toFixed(2)}_${wall.end.y.toFixed(2)}`;
    const reverseKey = `${wall.end.x.toFixed(2)}_${wall.end.y.toFixed(2)}_${wall.start.x.toFixed(2)}_${wall.start.y.toFixed(2)}`;
    
    if (!seenWalls.has(wallKey) && !seenWalls.has(reverseKey)) {
      uniqueWalls.push(wall);
      seenWalls.add(wallKey);
    } else {
      issuesFound.push(`Duplicate wall found: ${wall.tempId}`);
      issuesFixed.push(`Removed duplicate wall: ${wall.tempId}`);
      duplicatesRemoved++;
    }
  });

  return { walls: uniqueWalls, issuesFound, issuesFixed, duplicatesRemoved };
};

/**
 * Fix extrusion-related issues that cause lag and broken functionality
 */
const fixExtrusionIssues = (walls: WallWithTempId[]): {
  walls: WallWithTempId[];
  issuesFound: string[];
  issuesFixed: string[];
  extrusionsFixed: number;
} => {
  const issuesFound: string[] = [];
  const issuesFixed: string[] = [];
  let extrusionsFixed = 0;

  // Find all extrusion-related walls
  const mainSegments = walls.filter(w => w.isMainExtrusionSegment);
  const connectors = walls.filter(w => w.isExtrusionConnector);
  const splitWalls = walls.filter(w => w.isSplitOrigin);

  console.log('[FileRepair] Analyzing extrusions:', {
    mainSegments: mainSegments.length,
    connectors: connectors.length,
    splitWalls: splitWalls.length
  });

  let fixedWalls = [...walls];

  // 🔧 LEGACY FIX: Fix missing parent wall IDs for existing floor plans
  const connectorsNeedingFix = connectors.filter(connector => !(connector as any).parentWallId);
  if (connectorsNeedingFix.length > 0) {
    console.log(`[FileRepair] Found ${connectorsNeedingFix.length} extrusion connectors missing parent wall IDs - applying legacy fix`);
    
    // Pass 1: Try to find parent wall IDs based on geometric relationships
    fixedWalls = fixedWalls.map(wall => {
      if (wall.isExtrusionConnector && !(wall as any).parentWallId) {
        // Find the nearest main segment that might be the parent
        const nearbyMainSegments = mainSegments.filter(mainSeg => {
          return pointsAreEqual(wall.start, mainSeg.start, 1) ||
                 pointsAreEqual(wall.start, mainSeg.end, 1) ||
                 pointsAreEqual(wall.end, mainSeg.start, 1) ||
                 pointsAreEqual(wall.end, mainSeg.end, 1);
        });
        
        if (nearbyMainSegments.length > 0) {
          const parentMainSegment = nearbyMainSegments[0];
          const fallbackParentId = parentMainSegment.tempId || `${parentMainSegment.start.x.toFixed(1)}_${parentMainSegment.start.y.toFixed(1)}_${parentMainSegment.end.x.toFixed(1)}_${parentMainSegment.end.y.toFixed(1)}`;
          
          issuesFound.push(`Extrusion connector missing parent wall ID: ${wall.tempId}`);
          issuesFixed.push(`Applied legacy parent wall ID fix: ${fallbackParentId}`);
          extrusionsFixed++;
          
          return { ...wall, parentWallId: fallbackParentId };
        }
        
        // If no main segment found, try finding parent from other connectors
        const otherConnectors = connectors.filter(c => c.tempId !== wall.tempId && (c as any).parentWallId);
        if (otherConnectors.length > 0) {
          const nearbyConnector = otherConnectors.find(conn => {
            return pointsAreEqual(wall.start, conn.start, 1) ||
                   pointsAreEqual(wall.start, conn.end, 1) ||
                   pointsAreEqual(wall.end, conn.start, 1) ||
                   pointsAreEqual(wall.end, conn.end, 1);
          });
          
          if (nearbyConnector) {
            const fallbackParentId = (nearbyConnector as any).parentWallId;
            issuesFound.push(`Extrusion connector missing parent wall ID: ${wall.tempId}`);
            issuesFixed.push(`Applied parent wall ID from connected extrusion: ${fallbackParentId}`);
            extrusionsFixed++;
            
            return { ...wall, parentWallId: fallbackParentId };
          }
        }
        
        // Last resort: use a generic fallback
        const fallbackParentId = `legacy_parent_${wall.tempId}`;
        issuesFound.push(`Extrusion connector missing parent wall ID: ${wall.tempId}`);
        issuesFixed.push(`Applied fallback parent wall ID: ${fallbackParentId}`);
        extrusionsFixed++;
      
        return { ...wall, parentWallId: fallbackParentId };
      }
      
      return wall;
    });
    
    // Pass 2: Propagate parent wall IDs to connected split walls and main segments
    const connectorsWithParentIds = fixedWalls.filter(w => w.isExtrusionConnector && (w as any).parentWallId);
    
    fixedWalls = fixedWalls.map(wall => {
      if ((wall as any).parentWallId) return wall; // Already has parent wall ID
      
      // Check if this is a split wall or main segment connected to a fixed connector
      const connectedConnector = connectorsWithParentIds.find(connector => {
        return pointsAreEqual(wall.start, connector.start, 1) ||
               pointsAreEqual(wall.start, connector.end, 1) ||
               pointsAreEqual(wall.end, connector.start, 1) ||
               pointsAreEqual(wall.end, connector.end, 1);
      });
      
      if (connectedConnector) {
        const parentWallId = (connectedConnector as any).parentWallId;
        issuesFound.push(`Wall missing parent wall ID: ${wall.tempId}`);
        issuesFixed.push(`Propagated parent wall ID from connected extrusion: ${parentWallId}`);
        extrusionsFixed++;
        return { ...wall, parentWallId };
      }
      
      return wall;
    });
  }
  
  const wallsToRemove = new Set<string>();

  // Fix orphaned connectors (connectors without main segments)
  for (const connector of connectors) {
    const connectedMainSegments = mainSegments.filter(main => 
      pointsAreEqual(connector.start, main.start, POINT_MATCH_TOLERANCE) ||
      pointsAreEqual(connector.start, main.end, POINT_MATCH_TOLERANCE) ||
      pointsAreEqual(connector.end, main.start, POINT_MATCH_TOLERANCE) ||
      pointsAreEqual(connector.end, main.end, POINT_MATCH_TOLERANCE)
    );

    if (connectedMainSegments.length === 0) {
      issuesFound.push(`Orphaned extrusion connector found: ${connector.tempId}`);
      issuesFixed.push(`Removed orphaned extrusion connector: ${connector.tempId}`);
      wallsToRemove.add(connector.tempId!);
      extrusionsFixed++;
    }
  }

  // Fix main segments without proper connectors
  for (const mainSegment of mainSegments) {
    const connectedConnectors = connectors.filter(conn => 
      !wallsToRemove.has(conn.tempId!) && (
        pointsAreEqual(conn.start, mainSegment.start, POINT_MATCH_TOLERANCE) ||
        pointsAreEqual(conn.start, mainSegment.end, POINT_MATCH_TOLERANCE) ||
        pointsAreEqual(conn.end, mainSegment.start, POINT_MATCH_TOLERANCE) ||
        pointsAreEqual(conn.end, mainSegment.end, POINT_MATCH_TOLERANCE)
      )
    );

    if (connectedConnectors.length < 2) {
      issuesFound.push(`Main extrusion segment with insufficient connectors: ${mainSegment.tempId} (has ${connectedConnectors.length}, needs 2)`);
      
      // Try to repair by converting back to normal wall
      const wallIndex = fixedWalls.findIndex(w => w.tempId === mainSegment.tempId);
      if (wallIndex !== -1) {
        fixedWalls[wallIndex] = {
          ...fixedWalls[wallIndex],
          isMainExtrusionSegment: false,
          isSplitOrigin: false
        };
        issuesFixed.push(`Converted broken main extrusion segment back to normal wall: ${mainSegment.tempId}`);
        extrusionsFixed++;
      }
    }
  }

  // Remove walls marked for removal
  const finalWalls = fixedWalls.filter(w => !wallsToRemove.has(w.tempId!));

  return { walls: finalWalls, issuesFound, issuesFixed, extrusionsFixed };
};

/**
 * Remove orphaned walls that aren't connected to anything
 */
const removeOrphanedWalls = (walls: WallWithTempId[]): {
  walls: WallWithTempId[];
  issuesFound: string[];
  issuesFixed: string[];
  orphansRemoved: number;
} => {
  const issuesFound: string[] = [];
  const issuesFixed: string[] = [];
  let orphansRemoved = 0;

  // Build adjacency map
  const adjacencyMap = new Map<string, WallWithTempId[]>();
  const pointToString = (p: Point) => `${Math.round(p.x * 100) / 100}_${Math.round(p.y * 100) / 100}`;

  walls.forEach(wall => {
    const startKey = pointToString(wall.start);
    const endKey = pointToString(wall.end);

    if (!adjacencyMap.has(startKey)) {
      adjacencyMap.set(startKey, []);
    }
    if (!adjacencyMap.has(endKey)) {
      adjacencyMap.set(endKey, []);
    }

    adjacencyMap.get(startKey)!.push(wall);
    adjacencyMap.get(endKey)!.push(wall);
  });

  // Find walls that are completely isolated (both endpoints have no other connections)
  const isolatedWalls = walls.filter(wall => {
    const startKey = pointToString(wall.start);
    const endKey = pointToString(wall.end);
    
    const startConnections = adjacencyMap.get(startKey) || [];
    const endConnections = adjacencyMap.get(endKey) || [];
    
    // Check if this wall is the only one at both endpoints
    const isIsolated = startConnections.length === 1 && endConnections.length === 1 &&
                      startConnections[0].tempId === wall.tempId && endConnections[0].tempId === wall.tempId;
    
    return isIsolated;
  });

  const validWalls = walls.filter(wall => {
    const isIsolated = isolatedWalls.includes(wall);
    if (isIsolated) {
      issuesFound.push(`Orphaned wall found: ${wall.tempId}`);
      issuesFixed.push(`Removed orphaned wall: ${wall.tempId}`);
      orphansRemoved++;
      return false;
    }
    return true;
  });

  return { walls: validWalls, issuesFound, issuesFixed, orphansRemoved };
};

/**
 * Fix broken element references (like wallId) by finding valid replacements per floor
 */
const fixBrokenElementReferences = (
  elements: FloorPlanElement[],
  walls: WallWithTempId[]
): {
  elements: FloorPlanElement[];
  issuesFound: string[];
  issuesFixed: string[];
  referencesFixed: number;
} => {
  const issuesFound: string[] = [];
  const issuesFixed: string[] = [];
  let referencesFixed = 0;

  // Create floor-aware wall ID mapping for better reference fixing
  const wallIdsByFloor = new Map<number, Map<string, WallWithTempId>>();
  walls.forEach(wall => {
    if (wall.floor && wall.floor >= 1) {
      if (!wallIdsByFloor.has(wall.floor)) {
        wallIdsByFloor.set(wall.floor, new Map());
      }
      const floorMap = wallIdsByFloor.get(wall.floor)!;
      if (wall.tempId) floorMap.set(wall.tempId, wall);
      if (wall.id) floorMap.set(wall.id, wall);
    }
  });

  console.log('[FileRepair] Floor-aware element reference fixing starting', {
    totalElements: elements.length,
    availableFloors: Array.from(wallIdsByFloor.keys()).sort((a, b) => a - b),
    totalWallIdsMapped: walls.filter(w => w.tempId || w.id).length
  });

  const fixedElements = elements.map((element, index) => {
    let fixedElement = { ...element };

    // Check for elements that reference walls
    if ('wallId' in element) {
      const wallId = (element as any).wallId;
      const elementFloor = ('floor' in element) ? (element as any).floor : 1;
      
      if (!wallId || typeof wallId !== 'string') {
        issuesFound.push(`Element ${element.type} at index ${index} has invalid wallId: ${wallId}`);
        
        // Try to find a suitable wall on the same floor
        const floorWalls = wallIdsByFloor.get(elementFloor);
        if (floorWalls && floorWalls.size > 0) {
          const firstWall = Array.from(floorWalls.values())[0];
          const newWallId = firstWall.tempId || firstWall.id || 'wall_unknown';
          
          // Only assign wallId to elements that actually have this property
          if (element.type === 'window' || element.type === 'door' || element.type === 'radiator' || element.type === 'boiler') {
            (fixedElement as any).wallId = newWallId;
            issuesFixed.push(`Assigned wallId ${newWallId} to ${element.type} on floor ${elementFloor}`);
            referencesFixed++;
          }
        }
      } else {
        // Check if wallId exists on the element's floor
        const floorWalls = wallIdsByFloor.get(elementFloor);
        const wallExists = floorWalls?.has(wallId);
        
        if (!wallExists) {
          issuesFound.push(`Element ${element.type} at index ${index} references non-existent wall ${wallId} on floor ${elementFloor}`);
          
          // Try to find a replacement wall
          if ('position' in element && (element as any).position) {
            const elementPos = (element as any).position as Point;
            const nearestWall = findNearestWallOnFloor(elementPos, elementFloor, wallIdsByFloor);
            
            if (nearestWall && nearestWall.tempId) {
              const newWallId = nearestWall.tempId;
              issuesFixed.push(`Fixed ${element.type} wall reference from ${wallId} to ${newWallId} on floor ${elementFloor}`);
              referencesFixed++;
              
              // Only assign wallId to elements that actually have this property
              if (element.type === 'window' || element.type === 'door' || element.type === 'radiator' || element.type === 'boiler') {
                (fixedElement as any).wallId = newWallId;
              }
            } else {
              // Fallback to first available wall on same floor
              const floorWalls = wallIdsByFloor.get(elementFloor);
              if (floorWalls && floorWalls.size > 0) {
                const firstWall = Array.from(floorWalls.values())[0];
                const fallbackWallId = firstWall.tempId || firstWall.id || 'wall_unknown';
                
                issuesFixed.push(`Fixed ${element.type} wall reference from ${wallId} to fallback wall ${fallbackWallId} on floor ${elementFloor}`);
                referencesFixed++;
                
                // Only assign wallId to elements that actually have this property
                if (element.type === 'window' || element.type === 'door' || element.type === 'radiator' || element.type === 'boiler') {
                  (fixedElement as any).wallId = fallbackWallId;
                }
              } else {
                issuesFixed.push(`Could not find replacement wall for ${element.type} on floor ${elementFloor}, kept original reference`);
              }
            }
          }
        }
      }
    }

    // Additional element-specific validations and fixes
    switch (element.type) {
      case 'window':
      case 'door':
        // Ensure wall-attached elements have valid positions on walls
        if ('positionOnWall' in fixedElement) {
          const pos = (fixedElement as any).positionOnWall;
          if (typeof pos !== 'number' || pos < 0 || pos > 1) {
            issuesFound.push(`${element.type} at index ${index} has invalid positionOnWall: ${pos}`);
            (fixedElement as any).positionOnWall = 0.5;
            issuesFixed.push(`Reset positionOnWall for ${element.type} to 0.5`);
            referencesFixed++;
          }
        }
        break;
        
      case 'radiator':
      case 'boiler':
        // Validate radiator/boiler specific properties
        if ('side' in fixedElement) {
          const side = (fixedElement as any).side;
          if (side !== -1 && side !== 1) {
            issuesFound.push(`${element.type} at index ${index} has invalid side: ${side}`);
            (fixedElement as any).side = 1;
            issuesFixed.push(`Reset side for ${element.type} to 1`);
            referencesFixed++;
          }
        }
        break;
    }

    return fixedElement;
  });

  console.log('[FileRepair] Floor-aware element reference fixing completed', {
    elementsProcessed: elements.length,
    referencesFixed,
    finalElementTypes: getElementTypeBreakdown(fixedElements)
  });

  return { elements: fixedElements, issuesFound, issuesFixed, referencesFixed };
};

/**
 * Validate wall geometry and fix common issues
 */
const validateWallGeometry = (walls: WallWithTempId[]): {
  walls: WallWithTempId[];
  issuesFound: string[];
  issuesFixed: string[];
} => {
  const issuesFound: string[] = [];
  const issuesFixed: string[] = [];

  const fixedWalls = walls.map(wall => {
    let fixedWall = { ...wall };
    
    // Check for invalid coordinates
    if (!isFinite(wall.start.x) || !isFinite(wall.start.y) || !isFinite(wall.end.x) || !isFinite(wall.end.y)) {
      issuesFound.push(`Wall ${wall.tempId} has invalid coordinates`);
      // Reset to origin if coordinates are invalid
      fixedWall = {
        ...wall,
        start: { x: 0, y: 0 },
        end: { x: 50, y: 0 }
      };
      issuesFixed.push(`Reset invalid coordinates for wall ${wall.tempId}`);
    }
    
    // Ensure wall type is valid
    if (!wall.type || !['HLP', 'PW', 'SW', 'None'].includes(wall.type)) {
      issuesFound.push(`Wall ${wall.tempId} has invalid type: ${wall.type}`);
      fixedWall = { ...fixedWall, type: 'None' };
      issuesFixed.push(`Reset wall type to 'None' for wall ${wall.tempId}`);
    }
    
    // Ensure floor is valid
    if (!wall.floor || wall.floor < 1) {
      issuesFound.push(`Wall ${wall.tempId} has invalid floor: ${wall.floor}`);
      fixedWall = { ...fixedWall, floor: 1 };
      issuesFixed.push(`Reset floor to 1 for wall ${wall.tempId}`);
    }
    
    return fixedWall;
  });

  return { walls: fixedWalls, issuesFound, issuesFixed };
};

/**
 * Find the nearest wall to a point on a specific floor
 */
const findNearestWallOnFloor = (
  point: Point, 
  floor: number, 
  wallIdsByFloor: Map<number, Map<string, WallWithTempId>>
): WallWithTempId | null => {
  const floorWalls = wallIdsByFloor.get(floor);
  if (!floorWalls) return null;

  let nearestWall: WallWithTempId | null = null;
  let minDistance = Infinity;

  for (const wall of floorWalls.values()) {
    const distance = distanceToLine(point, wall.start, wall.end);
    if (distance < minDistance) {
      minDistance = distance;
      nearestWall = wall;
    }
  }

  return nearestWall;
};

/**
 * Calculate distance from point to line segment
 */
const distanceToLine = (point: Point, lineStart: Point, lineEnd: Point): number => {
  const lineLength = Math.sqrt(
    Math.pow(lineEnd.x - lineStart.x, 2) + 
    Math.pow(lineEnd.y - lineStart.y, 2)
  );
  
  if (lineLength === 0) return Math.sqrt(
    Math.pow(point.x - lineStart.x, 2) + 
    Math.pow(point.y - lineStart.y, 2)
  );

  const t = Math.max(0, Math.min(1, 
    ((point.x - lineStart.x) * (lineEnd.x - lineStart.x) + 
     (point.y - lineStart.y) * (lineEnd.y - lineStart.y)) / (lineLength * lineLength)
  ));

  const closestPoint = {
    x: lineStart.x + t * (lineEnd.x - lineStart.x),
    y: lineStart.y + t * (lineEnd.y - lineStart.y)
  };

  return Math.sqrt(
    Math.pow(point.x - closestPoint.x, 2) + 
    Math.pow(point.y - closestPoint.y, 2)
  );
};

/**
 * Complete incomplete rooms by adding missing walls to close gaps
 */
const completeIncompleteRooms = (walls: WallWithTempId[]): {
  walls: WallWithTempId[];
  issuesFound: string[];
  issuesFixed: string[];
  roomsCompleted: number;
  wallsAdded: number;
} => {
  const issuesFound: string[] = [];
  const issuesFixed: string[] = [];
  let roomsCompleted = 0;
  let wallsAdded = 0;

  console.log('[RoomCompletion] Starting incomplete room detection and completion...');

  // Build adjacency map to find disconnected endpoints
  const adjacencyMap = new Map<string, { point: Point, connectedWalls: WallWithTempId[] }>();
  const pointToString = (p: Point) => `${Math.round(p.x * 100) / 100}_${Math.round(p.y * 100) / 100}`;

  // Populate adjacency map
  walls.forEach(wall => {
    const startKey = pointToString(wall.start);
    const endKey = pointToString(wall.end);

    if (!adjacencyMap.has(startKey)) {
      adjacencyMap.set(startKey, { point: wall.start, connectedWalls: [] });
    }
    if (!adjacencyMap.has(endKey)) {
      adjacencyMap.set(endKey, { point: wall.end, connectedWalls: [] });
    }

    adjacencyMap.get(startKey)!.connectedWalls.push(wall);
    adjacencyMap.get(endKey)!.connectedWalls.push(wall);
  });

  // Find endpoints with only one connection (potential gaps)
  const disconnectedEndpoints: Array<{ point: Point; wall: WallWithTempId }> = [];
  
  adjacencyMap.forEach((nodeData, pointKey) => {
    if (nodeData.connectedWalls.length === 1) {
      // This is a dead end - could be part of an incomplete room
      disconnectedEndpoints.push({
        point: nodeData.point,
        wall: nodeData.connectedWalls[0]
      });
    }
  });

  console.log(`[RoomCompletion] Found ${disconnectedEndpoints.length} disconnected endpoints`);

  if (disconnectedEndpoints.length === 0) {
    return { walls, issuesFound, issuesFixed, roomsCompleted, wallsAdded };
  }

  // Group nearby disconnected endpoints that could form room completions
  const completionGroups = findRoomCompletionGroups(disconnectedEndpoints);
  
  console.log(`[RoomCompletion] Found ${completionGroups.length} potential room completion groups`);

  const newWalls: WallWithTempId[] = [];

  completionGroups.forEach((group, groupIndex) => {
    if (group.length < 2) return; // Need at least 2 endpoints to create a completion

    const completionWalls = createRoomCompletionWalls(group, walls);
    
    if (completionWalls.length > 0) {
      newWalls.push(...completionWalls);
      roomsCompleted++;
      wallsAdded += completionWalls.length;
      
      issuesFound.push(`Incomplete room detected with ${group.length} disconnected endpoints`);
      issuesFixed.push(`Added ${completionWalls.length} walls to complete room (group ${groupIndex + 1})`);
      
      console.log(`[RoomCompletion] Completed room ${groupIndex + 1}: added ${completionWalls.length} walls`);
    }
  });

  const completedWalls = [...walls, ...newWalls];
  
  console.log(`[RoomCompletion] Completed ${roomsCompleted} rooms by adding ${wallsAdded} walls`);

  return {
    walls: completedWalls,
    issuesFound,
    issuesFixed,
    roomsCompleted,
    wallsAdded
  };
};

/**
 * Find groups of disconnected endpoints that could form room completions
 */
const findRoomCompletionGroups = (
  disconnectedEndpoints: Array<{ point: Point; wall: WallWithTempId }>
): Array<Array<{ point: Point; wall: WallWithTempId }>> => {
  const groups: Array<Array<{ point: Point; wall: WallWithTempId }>> = [];
  const processed = new Set<number>();
  const MAX_GROUP_DISTANCE = 200; // Maximum distance between endpoints to consider them part of the same incomplete room

  disconnectedEndpoints.forEach((endpoint, index) => {
    if (processed.has(index)) return;

    const group = [endpoint];
    processed.add(index);

    // Find other endpoints that are close enough to be part of the same incomplete room
    disconnectedEndpoints.forEach((otherEndpoint, otherIndex) => {
      if (processed.has(otherIndex)) return;

      const distance = Math.hypot(
        endpoint.point.x - otherEndpoint.point.x,
        endpoint.point.y - otherEndpoint.point.y
      );

      if (distance <= MAX_GROUP_DISTANCE) {
        group.push(otherEndpoint);
        processed.add(otherIndex);
      }
    });

    if (group.length >= 2) {
      groups.push(group);
    }
  });

  return groups;
};

/**
 * Create walls to complete an incomplete room
 */
const createRoomCompletionWalls = (
  endpoints: Array<{ point: Point; wall: WallWithTempId }>,
  existingWalls: WallWithTempId[]
): WallWithTempId[] => {
  if (endpoints.length < 2) return [];

  const newWalls: WallWithTempId[] = [];
  const MIN_WALL_LENGTH = 20; // Minimum wall length to create
  const MAX_WALL_LENGTH = 300; // Maximum wall length to create

  // Strategy 1: For 2 endpoints, create a direct connection if reasonable
  if (endpoints.length === 2) {
    const [endpoint1, endpoint2] = endpoints;
    const distance = Math.hypot(
      endpoint1.point.x - endpoint2.point.x,
      endpoint1.point.y - endpoint2.point.y
    );

    if (distance >= MIN_WALL_LENGTH && distance <= MAX_WALL_LENGTH) {
      // Check if this wall would intersect with existing walls inappropriately
      const wouldCreateValidRoom = !wouldIntersectExistingWalls(
        endpoint1.point,
        endpoint2.point,
        existingWalls
      );

      if (wouldCreateValidRoom) {
        const newWall: WallWithTempId = {
          start: { ...endpoint1.point },
          end: { ...endpoint2.point },
          type: 'None',
          floor: endpoint1.wall.floor || 1,
          tempId: `completion-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        };
        newWalls.push(newWall);
      }
    }
  }
  
  // Strategy 2: For more endpoints, try to create a logical room shape
  else if (endpoints.length > 2) {
    // Sort endpoints to form a logical connection order
    const sortedEndpoints = sortEndpointsForRoomCompletion(endpoints);
    
    // Create walls between consecutive endpoints
    for (let i = 0; i < sortedEndpoints.length; i++) {
      const currentEndpoint = sortedEndpoints[i];
      const nextEndpoint = sortedEndpoints[(i + 1) % sortedEndpoints.length];
      
      const distance = Math.hypot(
        currentEndpoint.point.x - nextEndpoint.point.x,
        currentEndpoint.point.y - nextEndpoint.point.y
      );

      if (distance >= MIN_WALL_LENGTH && distance <= MAX_WALL_LENGTH) {
        const wouldCreateValidRoom = !wouldIntersectExistingWalls(
          currentEndpoint.point,
          nextEndpoint.point,
          existingWalls
        );

        if (wouldCreateValidRoom) {
          const newWall: WallWithTempId = {
            start: { ...currentEndpoint.point },
            end: { ...nextEndpoint.point },
            type: 'None',
            floor: currentEndpoint.wall.floor || 1,
            tempId: `completion-${Date.now()}-${i}-${Math.random().toString(36).substr(2, 9)}`
          };
          newWalls.push(newWall);
        }
      }
    }
  }

  return newWalls;
};

/**
 * Sort endpoints to create a logical room completion order
 */
const sortEndpointsForRoomCompletion = (
  endpoints: Array<{ point: Point; wall: WallWithTempId }>
): Array<{ point: Point; wall: WallWithTempId }> => {
  if (endpoints.length <= 2) return endpoints;

  // Calculate centroid of all endpoints
  const centroid = {
    x: endpoints.reduce((sum, ep) => sum + ep.point.x, 0) / endpoints.length,
    y: endpoints.reduce((sum, ep) => sum + ep.point.y, 0) / endpoints.length
  };

  // Sort by angle from centroid to create a clockwise ordering
  return endpoints.sort((a, b) => {
    const angleA = Math.atan2(a.point.y - centroid.y, a.point.x - centroid.x);
    const angleB = Math.atan2(b.point.y - centroid.y, b.point.x - centroid.x);
    return angleA - angleB;
  });
};

/**
 * Check if a proposed wall would intersect existing walls inappropriately
 */
const wouldIntersectExistingWalls = (
  start: Point,
  end: Point,
  existingWalls: WallWithTempId[]
): boolean => {
  const INTERSECTION_TOLERANCE = 5; // Allow small intersections at endpoints

  for (const wall of existingWalls) {
    // Skip if the wall shares an endpoint (expected connection)
    const sharesStartPoint = 
      Math.hypot(start.x - wall.start.x, start.y - wall.start.y) < INTERSECTION_TOLERANCE ||
      Math.hypot(start.x - wall.end.x, start.y - wall.end.y) < INTERSECTION_TOLERANCE;
      
    const sharesEndPoint = 
      Math.hypot(end.x - wall.start.x, end.y - wall.start.y) < INTERSECTION_TOLERANCE ||
      Math.hypot(end.x - wall.end.x, end.y - wall.end.y) < INTERSECTION_TOLERANCE;

    if (sharesStartPoint || sharesEndPoint) continue;

    // Check for line intersection
    if (doLinesIntersect(start, end, wall.start, wall.end)) {
      return true; // Would intersect inappropriately
    }
  }

  return false; // No problematic intersections
};

/**
 * Check if two line segments intersect
 */
const doLinesIntersect = (p1: Point, q1: Point, p2: Point, q2: Point): boolean => {
  const orientation = (p: Point, q: Point, r: Point): number => {
    const val = (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y);
    if (val === 0) return 0; // Collinear
    return val > 0 ? 1 : 2; // Clockwise or Counterclockwise
  };

  const onSegment = (p: Point, q: Point, r: Point): boolean => {
    return q.x <= Math.max(p.x, r.x) && q.x >= Math.min(p.x, r.x) &&
           q.y <= Math.max(p.y, r.y) && q.y >= Math.min(p.y, r.y);
  };

  const o1 = orientation(p1, q1, p2);
  const o2 = orientation(p1, q1, q2);
  const o3 = orientation(p2, q2, p1);
  const o4 = orientation(p2, q2, q1);

  // General case
  if (o1 !== o2 && o3 !== o4) return true;

  // Special cases - collinear points
  if (o1 === 0 && onSegment(p1, p2, q1)) return true;
  if (o2 === 0 && onSegment(p1, q2, q1)) return true;
  if (o3 === 0 && onSegment(p2, p1, q2)) return true;
  if (o4 === 0 && onSegment(p2, q1, q2)) return true;

  return false;
};

/**
 * Label unnamed rooms with appropriate names based on their characteristics
 */
const labelUnnamedRooms = (
  walls: WallWithTempId[],
  existingRoomLabels: Record<string, string>
): {
  roomLabels: Record<string, string>;
  issuesFound: string[];
  issuesFixed: string[];
  roomsLabeled: number;
} => {
  const issuesFound: string[] = [];
  const issuesFixed: string[] = [];
  let roomsLabeled = 0;

  // Calculate rooms from walls
  const rooms = calculateRooms(walls, {});
  const updatedRoomLabels = { ...existingRoomLabels };

  // Room naming logic based on area and characteristics
  const getRoomName = (room: Room, index: number): string => {
    const area = room.area;
    
    // Very small rooms (< 3m²) - likely cupboards or storage
    if (area < 3) {
      return `Cupboard ${index + 1}`;
    }
    // Small rooms (3-6m²) - likely bathrooms, WCs, or small storage
    else if (area < 6) {
      return `Bathroom ${index + 1}`;
    }
    // Medium rooms (6-15m²) - likely bedrooms or studies
    else if (area < 15) {
      return `Bedroom ${index + 1}`;
    }
    // Large rooms (15-30m²) - likely living rooms or kitchens
    else if (area < 30) {
      return `Living Room ${index + 1}`;
    }
    // Very large rooms (30m²+) - likely open plan areas
    else {
      return `Open Plan Area ${index + 1}`;
    }
  };

  // Check each room for missing labels
  rooms.forEach((room, index) => {
    const hasExistingLabel = room.stableId && updatedRoomLabels[room.stableId];
    const hasValidName = room.name && room.name.trim() !== '' && !room.name.match(/^Room \d+$/);
    
    if (!hasExistingLabel && !hasValidName) {
      const suggestedName = getRoomName(room, index);
      
      if (room.stableId) {
        updatedRoomLabels[room.stableId] = suggestedName;
        issuesFound.push(`Room ${room.stableId} has no name (area: ${room.area.toFixed(2)}m²)`);
        issuesFixed.push(`Labeled room ${room.stableId} as "${suggestedName}"`);
        roomsLabeled++;
      } else {
        issuesFound.push(`Room at index ${index} has no stable ID and cannot be labeled`);
      }
    }
  });

  return { roomLabels: updatedRoomLabels, issuesFound, issuesFixed, roomsLabeled };
}; 

/**
 * Generate a comprehensive repair report
 */
export const generateRepairReport = (result: FileRepairResult): string => {
  const { reportSummary, issuesFound, issuesFixed } = result;
  
  let report = '=== COMPREHENSIVE FLOOR PLAN FILE REPAIR REPORT ===\n\n';
  
  report += `SUMMARY:\n`;
  report += `- Total walls processed: ${reportSummary.totalWalls}\n`;
  report += `- Total elements processed: ${reportSummary.totalElements}\n`;
  report += `- Wall issues found: ${reportSummary.wallsWithIssues}\n`;
  report += `- Element issues found: ${reportSummary.elementsWithIssues}\n`;
  report += `- Walls fixed: ${reportSummary.wallsFixed}\n`;
  report += `- Elements fixed: ${reportSummary.elementsFixed}\n`;
  report += `- Extrusions fixed: ${reportSummary.extrusionsFixed}\n`;
  report += `- Orphaned walls removed: ${reportSummary.orphanedWallsRemoved}\n`;
  report += `- Duplicate walls removed: ${reportSummary.duplicateWallsRemoved}\n`;
  report += `- Wall temp IDs generated: ${reportSummary.tempIdsGenerated}\n`;
  report += `- Element IDs generated: ${reportSummary.elementIdsGenerated}\n`;
  report += `- Element references fixed: ${reportSummary.brokenElementReferencesFixed}\n`;
  report += `- Element properties fixed: ${reportSummary.elementPropertiesFixed}\n`;
  report += `- Elements relocated to valid floors: ${reportSummary.elementsRelocatedToValidFloors}\n`;
  report += `- Unnamed rooms labeled: ${reportSummary.unnamedRoomsLabeled}\n`;
  report += `- Incomplete rooms completed: ${reportSummary.incompleteRoomsCompleted}\n`;
  report += `- Missing walls added: ${reportSummary.missingWallsAdded}\n\n`;
  
  if (issuesFound.length > 0) {
    report += `ISSUES FOUND (${issuesFound.length}):\n`;
    issuesFound.forEach((issue, index) => {
      report += `${index + 1}. ${issue}\n`;
    });
    report += '\n';
  }
  
  if (issuesFixed.length > 0) {
    report += `FIXES APPLIED (${issuesFixed.length}):\n`;
    issuesFixed.forEach((fix, index) => {
      report += `${index + 1}. ${fix}\n`;
    });
    report += '\n';
  }
  
  const totalIssuesFixed = reportSummary.wallsFixed + reportSummary.elementsFixed + 
                          reportSummary.extrusionsFixed + reportSummary.orphanedWallsRemoved + 
                          reportSummary.duplicateWallsRemoved + reportSummary.tempIdsGenerated + 
                          reportSummary.elementIdsGenerated + reportSummary.brokenElementReferencesFixed + 
                          reportSummary.elementPropertiesFixed + reportSummary.elementsRelocatedToValidFloors;
  
  report += `RESULT: ${totalIssuesFixed > 0 ? `File successfully repaired! Fixed ${totalIssuesFixed} issues.` : 'No issues found - file is healthy!'}\n`;
  
  return report;
}; 